import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useOrgId } from '@/hooks/useOrgContext';
import { emitDeaAudit } from '@/lib/deaAudit';
import {
  ArrowLeft,
  Shield,
  Save,
  AlertTriangle,
  UserCheck,
} from 'lucide-react';

// ── Exact enums from CHECK constraints ──────────────────────────────────────

const TRANSACTION_TYPES = [
  { value: 'receipt', label: 'Receipt' },
  { value: 'administration', label: 'Administration' },
  { value: 'waste', label: 'Waste' },
  { value: 'transfer_in', label: 'Transfer In' },
  { value: 'transfer_out', label: 'Transfer Out' },
  { value: 'return', label: 'Return' },
  { value: 'destruction', label: 'Destruction' },
  { value: 'theft_loss', label: 'Theft / Loss' },
  { value: 'inventory_adjustment', label: 'Inventory Adjustment' },
  { value: 'emergency_use', label: 'Emergency Use' },
] as const;

const DEA_SCHEDULES = ['I', 'II', 'III', 'IV', 'V'] as const;

const UNITS = ['mg', 'mL', 'mcg', 'units', 'tablets', 'patches', 'vials', 'ampules'] as const;

// ── Which quantity field each type writes ──────────────────────────────────

function getQuantityField(type: string): string {
  switch (type) {
    case 'receipt':
    case 'transfer_in':
    case 'return':
      return 'quantity_received';
    case 'administration':
    case 'emergency_use':
      return 'quantity_administered';
    case 'waste':
    case 'transfer_out':
    case 'destruction':
      return 'quantity_wasted';
    case 'theft_loss':
      return 'discrepancy_amount';
    case 'inventory_adjustment':
      return 'quantity_remaining';
    default:
      return 'quantity_administered';
  }
}

// ── Types needing specific fields ──────────────────────────────────────────

const RECEIPT_TYPES = ['receipt', 'transfer_in', 'return'];
const PATIENT_TYPES = ['administration', 'waste', 'emergency_use'];
const WITNESS_REQUIRED = ['waste'];
const SCHEDULE_II_222_TYPES = ['receipt'];

// ── Component ──────────────────────────────────────────────────────────────

export function DEAEntryRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const org_id = useOrgId();

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [transactionType, setTransactionType] = useState('receipt');
  const [drugName, setDrugName] = useState('');
  const [drugStrength, setDrugStrength] = useState('');
  const [drugForm, setDrugForm] = useState('');
  const [ndcCode, setNdcCode] = useState('');
  const [deaSchedule, setDeaSchedule] = useState('II');
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [quantityRemaining, setQuantityRemaining] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('mg');
  const [providerName, setProviderName] = useState('');
  const [providerId, setProviderId] = useState('');
  const [providerDeaNumber, setProviderDeaNumber] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientId, setPatientId] = useState('');
  const [orCaseId, setOrCaseId] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [form222Number, setForm222Number] = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [discrepancyNotes, setDiscrepancyNotes] = useState('');
  const [notes, setNotes] = useState('');

  const requiresWitness = WITNESS_REQUIRED.includes(transactionType);
  const isReceiptType = RECEIPT_TYPES.includes(transactionType);
  const isPatientType = PATIENT_TYPES.includes(transactionType);
  const requiresForm222 = SCHEDULE_II_222_TYPES.includes(transactionType) && deaSchedule === 'II';

  // Fetch providers for witness/provider selection
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('provider_id, first_name, last_name, dea_number')
        .order('last_name');
      if (error) throw error;
      return data as { provider_id: string; first_name: string; last_name: string; dea_number: string | null }[];
    },
  });

  // Fetch active cases for case linking
  const { data: activeCases } = useQuery({
    queryKey: ['active-cases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_cases')
        .select('case_id, patient_name, procedure_name, patient_id')
        .in('case_status', ['scheduled', 'pre-op', 'in-progress'])
        .order('scheduled_start', { ascending: false });
      if (error) return [];
      return data as { case_id: string; patient_name: string; procedure_name: string; patient_id: string | null }[];
    },
  });

  // Fetch current inventory for the selected drug to get inventory_before
  const { data: currentInventory } = useQuery({
    queryKey: ['dea-current-inv', drugName, lotNumber],
    queryFn: async () => {
      if (!drugName) return null;
      let query = supabase
        .from('dea_current_inventory')
        .select('current_quantity')
        .eq('drug_name', drugName);
      if (lotNumber) query = query.eq('lot_number', lotNumber);
      const { data } = await query.limit(1).single();
      return data?.current_quantity ?? null;
    },
    enabled: !!drugName,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const newErrors: Record<string, string> = {};

      // Required fields
      if (!transactionType) newErrors.transactionType = 'Required';
      if (!drugName.trim()) newErrors.drugName = 'Required';
      if (!deaSchedule) newErrors.deaSchedule = 'Required';
      if (!unitOfMeasure) newErrors.unitOfMeasure = 'Required';
      if (!providerName.trim()) newErrors.providerName = 'Required';

      const qtyNum = parseFloat(quantity);
      const qtyRemNum = parseFloat(quantityRemaining);

      if (transactionType !== 'inventory_adjustment') {
        if (!quantity || isNaN(qtyNum) || qtyNum <= 0) newErrors.quantity = 'Valid positive number required';
      }
      if (!quantityRemaining || isNaN(qtyRemNum) || qtyRemNum < 0) {
        newErrors.quantityRemaining = 'Valid non-negative number required';
      }

      // Witness required for waste
      if (requiresWitness && (!witnessId || !witnessName.trim())) {
        newErrors.witness = 'Witness co-sign required for waste transactions';
      }

      // Receipt-specific
      if (isReceiptType && !supplierName.trim()) newErrors.supplierName = 'Required for receipts';
      if (requiresForm222 && !form222Number.trim()) newErrors.form222Number = 'Required for Schedule II receipts';

      // Org context
      if (!org_id) {
        newErrors.form = 'Organization context not loaded — try again.';
      }
      if (!user?.id) {
        newErrors.form = 'Not authenticated.';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        throw new Error('Validation failed');
      }

      // Build the insert row
      const qtyField = getQuantityField(transactionType);
      const quantityValue = transactionType === 'inventory_adjustment' ? qtyRemNum : qtyNum;

      const row: Record<string, unknown> = {
        org_id,
        transaction_type: transactionType,
        transaction_date: new Date().toISOString(),
        drug_name: drugName.trim(),
        drug_strength: drugStrength.trim() || null,
        drug_form: drugForm.trim() || null,
        ndc_code: ndcCode.trim() || null,
        dea_schedule: deaSchedule,
        lot_number: lotNumber.trim() || null,
        expiration_date: expirationDate || null,
        unit_of_measure: unitOfMeasure,
        quantity_remaining: qtyRemNum,
        provider_id: providerId || null,
        provider_name: providerName.trim(),
        provider_dea_number: providerDeaNumber.trim() || null,
        created_by_id: user!.id,
        created_by_name: user!.email ?? 'Unknown',
        notes: notes.trim() || null,
      };

      // Set the correct quantity field
      row[qtyField] = quantityValue;

      // Patient fields
      if (isPatientType) {
        row.patient_id = patientId || null;
        row.patient_name = patientName.trim() || null;
        row.or_case_id = orCaseId || null;
        row.procedure_name = procedureName.trim() || null;
      }

      // Receipt fields
      if (isReceiptType) {
        row.supplier_name = supplierName.trim();
        row.invoice_number = invoiceNumber.trim() || null;
        row.purchase_order_number = purchaseOrderNumber.trim() || null;
        if (requiresForm222) {
          row.form_222_number = form222Number.trim();
          row.requires_dea_222 = true;
        }
      }

      // Witness fields (waste)
      if (requiresWitness) {
        row.witness_id = witnessId;
        row.witness_name = witnessName.trim();
        row.witness_signature_timestamp = new Date().toISOString();
        row.witness_verified = true;
      }

      // Theft/loss discrepancy
      if (transactionType === 'theft_loss') {
        row.discrepancy_flag = true;
        row.discrepancy_type = 'theft_loss';
        row.discrepancy_notes = discrepancyNotes.trim() || null;
      }

      const { data: inserted, error } = await supabase
        .from('dea_controlled_substance_log')
        .insert(row)
        .select('log_id, running_balance')
        .single();

      if (error) throw error;

      // Fire-and-forget audit emission — durable insert into dea_access_log
      emitDeaAudit({
        orgId: org_id!,
        userId: user!.id,
        userEmail: user!.email ?? '',
        actionType: transactionType,
        drugName: drugName.trim(),
        ndc: ndcCode.trim() || null,
        strength: drugStrength.trim() || null,
        schedule: deaSchedule,
        quantityDispensed: quantityValue,
        patientName: patientName.trim() || null,
        prescriberDea: providerDeaNumber.trim() || null,
        inventoryBefore: currentInventory ?? null,
        inventoryAfter: inserted.running_balance ?? null,
        deaLogId: inserted.log_id,
      });

      return inserted;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dea-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dea-current-inv'] });
      navigate(`/dea-log/${data.log_id}`);
    },
  });

  const handleProviderChange = (pid: string) => {
    setProviderId(pid);
    const p = providers?.find((pr) => pr.provider_id === pid);
    if (p) {
      setProviderName(`${p.first_name} ${p.last_name}`);
      setProviderDeaNumber(p.dea_number ?? '');
    }
  };

  const handleWitnessChange = (pid: string) => {
    setWitnessId(pid);
    const p = providers?.find((pr) => pr.provider_id === pid);
    if (p) setWitnessName(`${p.first_name} ${p.last_name}`);
  };

  const handleCaseChange = (caseId: string) => {
    setOrCaseId(caseId);
    const c = activeCases?.find((ac) => ac.case_id === caseId);
    if (c) {
      setPatientName(c.patient_name);
      setPatientId(c.patient_id ?? '');
      setProcedureName(c.procedure_name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    createMutation.mutate();
  };

  const inputClass =
    'w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text placeholder-pt-text-dim focus:outline-none focus:border-pt-gold';
  const labelClass = 'block text-sm text-pt-text-muted mb-1';

  return (
    <div className="min-h-full">
      {/* Header */}
      <header className="border-b border-pt-border/60">
        <div className="max-w-[1000px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dea-log')}
              className="text-pt-text-muted hover:text-pt-gold transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <Shield className="w-6 h-6 text-pt-gold" />
            <div>
              <h1 className="text-lg font-display font-semibold">
                New DEA <span className="text-pt-gold">Transaction</span>
              </h1>
              <p className="text-xs text-pt-text-dim">21 CFR 1304.04 Compliant</p>
            </div>
          </div>
          <span className="text-xs text-pt-text-muted">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 py-6">
        {errors.form && (
          <div className="mb-4 p-3 rounded border bg-pt-red/10 border-pt-red/30 text-pt-red text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {errors.form}
          </div>
        )}

        {createMutation.isError && !errors.form && (
          <div className="mb-4 p-3 rounded border bg-pt-red/10 border-pt-red/30 text-pt-red text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {(createMutation.error as Error)?.message === 'Validation failed'
              ? 'Please correct the errors below.'
              : `Save failed: ${(createMutation.error as Error)?.message}`}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ── Transaction Type ─────────────────────────────────────── */}
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
            <h2 className="text-base font-semibold mb-4">Transaction Type</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {TRANSACTION_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTransactionType(t.value)}
                  className={`text-sm px-3 py-2 rounded border transition-colors ${
                    transactionType === t.value
                      ? 'bg-pt-gold/20 border-pt-gold text-pt-gold font-semibold'
                      : 'bg-pt-dark border-pt-input-border text-pt-text-muted hover:border-pt-gold/50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {errors.transactionType && (
              <p className="text-pt-red text-xs mt-1">{errors.transactionType}</p>
            )}
          </div>

          {/* ── Drug Information ─────────────────────────────────────── */}
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
            <h2 className="text-base font-semibold mb-4">Drug Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>
                  Drug Name <span className="text-pt-red">*</span>
                </label>
                <input
                  value={drugName}
                  onChange={(e) => setDrugName(e.target.value)}
                  placeholder="e.g. Fentanyl"
                  className={inputClass}
                />
                {errors.drugName && <p className="text-pt-red text-xs mt-1">{errors.drugName}</p>}
              </div>
              <div>
                <label className={labelClass}>Strength</label>
                <input
                  value={drugStrength}
                  onChange={(e) => setDrugStrength(e.target.value)}
                  placeholder="e.g. 50 mcg/mL"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Drug Form</label>
                <input
                  value={drugForm}
                  onChange={(e) => setDrugForm(e.target.value)}
                  placeholder="e.g. Injectable"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>NDC Code</label>
                <input
                  value={ndcCode}
                  onChange={(e) => setNdcCode(e.target.value)}
                  placeholder="XXXXX-XXXX-XX"
                  className={`${inputClass} font-mono`}
                />
              </div>
              <div>
                <label className={labelClass}>
                  DEA Schedule <span className="text-pt-red">*</span>
                </label>
                <select
                  value={deaSchedule}
                  onChange={(e) => setDeaSchedule(e.target.value)}
                  className={inputClass}
                >
                  {DEA_SCHEDULES.map((s) => (
                    <option key={s} value={s}>
                      Schedule {s}
                    </option>
                  ))}
                </select>
                {errors.deaSchedule && (
                  <p className="text-pt-red text-xs mt-1">{errors.deaSchedule}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Lot Number</label>
                <input
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  className={`${inputClass} font-mono`}
                />
              </div>
              <div>
                <label className={labelClass}>Expiration Date</label>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* ── Quantity ─────────────────────────────────────────────── */}
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
            <h2 className="text-base font-semibold mb-4">Quantity</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {transactionType !== 'inventory_adjustment' && (
                <div>
                  <label className={labelClass}>
                    {transactionType === 'theft_loss' ? 'Discrepancy Amount' : 'Quantity'}{' '}
                    <span className="text-pt-red">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className={`${inputClass} font-mono`}
                  />
                  {errors.quantity && <p className="text-pt-red text-xs mt-1">{errors.quantity}</p>}
                </div>
              )}
              <div>
                <label className={labelClass}>
                  Quantity Remaining (this vial/lot) <span className="text-pt-red">*</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={quantityRemaining}
                  onChange={(e) => setQuantityRemaining(e.target.value)}
                  placeholder={transactionType === 'inventory_adjustment' ? 'Physical count' : ''}
                  className={`${inputClass} font-mono`}
                />
                {errors.quantityRemaining && (
                  <p className="text-pt-red text-xs mt-1">{errors.quantityRemaining}</p>
                )}
                {transactionType === 'inventory_adjustment' && (
                  <p className="text-pt-text-dim text-xs mt-1">
                    Enter the physical count — this becomes the new balance.
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>
                  Unit of Measure <span className="text-pt-red">*</span>
                </label>
                <select
                  value={unitOfMeasure}
                  onChange={(e) => setUnitOfMeasure(e.target.value)}
                  className={inputClass}
                >
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                {errors.unitOfMeasure && (
                  <p className="text-pt-red text-xs mt-1">{errors.unitOfMeasure}</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Provider ─────────────────────────────────────────────── */}
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
            <h2 className="text-base font-semibold mb-4">Provider</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Provider <span className="text-pt-red">*</span>
                </label>
                <select
                  value={providerId}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select provider…</option>
                  {providers?.map((p) => (
                    <option key={p.provider_id} value={p.provider_id}>
                      {p.first_name} {p.last_name}
                    </option>
                  ))}
                </select>
                {!providerId && (
                  <input
                    value={providerName}
                    onChange={(e) => setProviderName(e.target.value)}
                    placeholder="Or type name manually"
                    className={`${inputClass} mt-2`}
                  />
                )}
                {errors.providerName && (
                  <p className="text-pt-red text-xs mt-1">{errors.providerName}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Provider DEA Number</label>
                <input
                  value={providerDeaNumber}
                  onChange={(e) => setProviderDeaNumber(e.target.value)}
                  className={`${inputClass} font-mono`}
                />
              </div>
            </div>
          </div>

          {/* ── Patient / Case (administration, waste, emergency_use) ── */}
          {isPatientType && (
            <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
              <h2 className="text-base font-semibold mb-4">Patient / Case</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Link to OR Case</label>
                  <select
                    value={orCaseId}
                    onChange={(e) => handleCaseChange(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select active case…</option>
                    {activeCases?.map((c) => (
                      <option key={c.case_id} value={c.case_id}>
                        {c.patient_name} — {c.procedure_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Patient Name</label>
                  <input
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Procedure</label>
                  <input
                    value={procedureName}
                    onChange={(e) => setProcedureName(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Supply Chain (receipt types) ──────────────────────────── */}
          {isReceiptType && (
            <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
              <h2 className="text-base font-semibold mb-4">Supply Chain</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Supplier <span className="text-pt-red">*</span>
                  </label>
                  <input
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    className={inputClass}
                  />
                  {errors.supplierName && (
                    <p className="text-pt-red text-xs mt-1">{errors.supplierName}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Invoice Number</label>
                  <input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    className={`${inputClass} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelClass}>Purchase Order Number</label>
                  <input
                    value={purchaseOrderNumber}
                    onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                    className={`${inputClass} font-mono`}
                  />
                </div>
                {requiresForm222 && (
                  <div>
                    <label className={labelClass}>
                      DEA Form 222 Number <span className="text-pt-red">*</span>
                    </label>
                    <input
                      value={form222Number}
                      onChange={(e) => setForm222Number(e.target.value)}
                      className={`${inputClass} font-mono`}
                    />
                    {errors.form222Number && (
                      <p className="text-pt-red text-xs mt-1">{errors.form222Number}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Witness Co-Sign (waste) ───────────────────────────────── */}
          {requiresWitness && (
            <div className="bg-pt-amber/5 border border-pt-amber/30 rounded-lg p-6">
              <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-pt-amber" />
                Witness Co-Sign <span className="text-pt-red">*</span>
              </h2>
              <p className="text-sm text-pt-text-muted mb-4">
                Waste transactions require verification by a second licensed provider.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Witness Provider</label>
                  <select
                    value={witnessId}
                    onChange={(e) => handleWitnessChange(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select witness…</option>
                    {providers
                      ?.filter((p) => p.provider_id !== providerId)
                      .map((p) => (
                        <option key={p.provider_id} value={p.provider_id}>
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Witness Name</label>
                  <input
                    value={witnessName}
                    onChange={(e) => setWitnessName(e.target.value)}
                    readOnly={!!witnessId}
                    className={inputClass}
                  />
                </div>
              </div>
              {errors.witness && (
                <p className="text-pt-red text-xs mt-2">{errors.witness}</p>
              )}
            </div>
          )}

          {/* ── Theft/Loss Notes ─────────────────────────────────────── */}
          {transactionType === 'theft_loss' && (
            <div className="bg-pt-red/5 border border-pt-red/30 rounded-lg p-6">
              <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-pt-red" />
                Theft / Loss Report
              </h2>
              <textarea
                value={discrepancyNotes}
                onChange={(e) => setDiscrepancyNotes(e.target.value)}
                rows={4}
                placeholder="Describe the circumstances of the loss…"
                className={inputClass}
              />
            </div>
          )}

          {/* ── Notes ─────────────────────────────────────────────────── */}
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6">
            <h2 className="text-base font-semibold mb-4">Notes</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes…"
              className={inputClass}
            />
          </div>

          {/* ── Submit ─────────────────────────────────────────────────── */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 bg-pt-gold/20 text-pt-gold px-6 py-2.5 rounded border border-pt-gold/30 hover:bg-pt-gold/30 font-semibold disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {createMutation.isPending ? 'Saving…' : 'Post Transaction'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dea-log')}
              className="text-pt-text-muted hover:text-pt-text text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
